import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RunCard, type RunCardData } from './RunCard.js';
import '../../styles/print.css';
// Sawyer 06 beds-lockers-runcard (B4, refuter #1) — "Edit card" on the day deck.
import { readSession } from '../../lib/api.js';
import { effectiveRolesOf, rolesHavePermission } from '../../lib/rbac-matrix.js';
import { RunCardEditor } from './RunCardEditor.js';
import './StayLockerSection.css';

/**
 * Sawyer 06 beds-lockers-runcard (B4) — what the day deck needs to offer
 * "Edit card" per card: the editor's API, the deck's night, and how to
 * reload the deck from the server after a save (what prints is what the
 * server now holds, never a client-side guess).
 */
export interface RunCardDeckEdit {
  readonly apiUrl: string;
  readonly token: string;
  /** YYYY-MM-DD, the night the deck prints. */
  readonly night?: string;
  readonly reload: () => Promise<void>;
}

interface RunCardPrintLayoutProps {
  readonly cards: ReadonlyArray<RunCardData>;
  readonly dateLabel: string;
  readonly onClose: () => void;
  /** Auto-trigger the browser print dialog as soon as the layout mounts. */
  readonly autoPrint?: boolean;
  /**
   * Ted #46 (#1218) — called whenever these cards go to the printer (the
   * Print button, or autoPrint), so the caller can record each visit's
   * printed card. The layout stays dumb: it says WHEN, the caller decides
   * what that means.
   */
  readonly onPrint?: () => void;
  /**
   * Ted #46 — reload this deck as today's version. Offered only when a card
   * here is a visit's printed record and the dog's profile has changed since.
   */
  readonly onUseLive?: () => void;
  /**
   * Sawyer 06 beds-lockers-runcard (B4) — when set, each card gets a
   * screen-only "Edit card" (for roles that can write the stay), opening the
   * run-card editor IN this panel, never a dialog on a dialog.
   */
  readonly edit?: RunCardDeckEdit;
}

/**
 * Print-friendly container for one or more `RunCard` components.
 *
 * Screen view: a full-screen overlay with a stacked preview and a sticky
 * toolbar (Print / Close).
 * Print view: every card lands on its own US-Letter page; the toolbar
 * and everything outside `[data-print-root]` is hidden by `print.css`.
 *
 * The overlay renders through a PORTAL to document.body, same as the
 * arrivals board. print.css hides `body > *:not([data-print-root])`, which
 * only spares a DIRECT child of <body> — rendered in place, the overlay sat
 * inside #root, #root went `display: none` under print media, and every
 * card printed as a blank page (pre-existing since #101 / #27). Owning the
 * portal here means no caller can mount the layout somewhere unprintable.
 *
 * The component is dumb on purpose — the caller (`Bookings.tsx`)
 * sources card data, sorts, and decides what counts as "boarding today."
 */
export function RunCardPrintLayout({
  cards,
  dateLabel,
  onClose,
  autoPrint = false,
  onPrint,
  onUseLive,
  edit,
}: RunCardPrintLayoutProps): JSX.Element {
  // Sawyer 06 beds-lockers-runcard (B4) — the card whose editor is open, or null.
  const [editingId, setEditingId] = useState<string | null>(null);
  const canEdit =
    edit !== undefined &&
    rolesHavePermission(effectiveRolesOf(readSession().staff), 'bookings.daycare-boarding:write:all');
  const editingCard = canEdit && editingId ? cards.find((c) => c.cardId === editingId) ?? null : null;
  // The latest callback, without re-running the autoPrint effect when a
  // caller passes a fresh closure each render.
  const onPrintRef = useRef(onPrint);
  onPrintRef.current = onPrint;
  useEffect(() => {
    if (!autoPrint) return;
    // Defer to next frame so the cards are in the DOM before the OS
    // print dialog snapshots the page.
    const id = window.requestAnimationFrame(() => {
      onPrintRef.current?.();
      window.print();
    });
    return () => window.cancelAnimationFrame(id);
  }, [autoPrint]);

  // Ted #46 — reprints of a visit's record whose dog has changed since.
  const changed = cards.filter((c) => c.visitRecord?.changedSincePrint);

  const layout = (
    <div
      className="ferro-print-overlay"
      data-print-root="true"
      data-testid="run-cards-overlay"
    >
      <div
        className="ferro-print-root"
        data-testid="run-card-print-layout"
        role="region"
        aria-label="Run cards print preview"
      >
        <div className="ferro-print-toolbar">
          <div>
            <strong>Run cards · {dateLabel}</strong>
            <span style={{ marginLeft: '12px', opacity: 0.65 }}>
              {cards.length} card{cards.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="ferro-print-toolbar__actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={onClose}
              data-testid="run-cards-close"
            >
              Close
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              disabled={editingCard !== null}
              onClick={() => {
                onPrint?.();
                window.print();
              }}
              data-testid="run-cards-print"
            >
              Print
            </button>
          </div>
        </div>

        {/* Ted #46 — a reprint is the card AS PRINTED for the visit. When
            the dog's profile has changed since, say so before anyone prints
            (Principle 5) and offer today's version as a deliberate choice.
            Screen only: print.css hides the toolbar region, and the card
            itself carries the same fact on paper. */}
        {changed.length > 0 ? (
          <div className="ferro-print-notice" role="status" data-testid="run-cards-changed-notice">
            <p>
              {changed.length === 1
                ? `${changed[0]!.dogName}’s card reprints as it was printed for this visit. Their profile has changed since.`
                : `${changed.length} cards reprint as they were printed for this visit. Those dogs’ profiles have changed since.`}
            </p>
            {onUseLive ? (
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={onUseLive}
                data-testid="run-cards-use-live"
              >
                Print today’s version
              </button>
            ) : null}
          </div>
        ) : null}

        {/* Sawyer 06 beds-lockers-runcard (B4) — the editor replaces the stack while open. */}
        {editingCard && edit ? (
          <div className="ferro-print-editor" data-testid="run-cards-editor-panel">
            <RunCardEditor
              apiUrl={edit.apiUrl}
              token={edit.token}
              bookingId={editingCard.bookingId}
              dogId={editingCard.dogId}
              dogName={editingCard.dogName}
              night={edit.night}
              onBack={() => setEditingId(null)}
              onSaved={async () => {
                await edit.reload();
                setEditingId(null);
              }}
            />
          </div>
        ) : cards.length === 0 ? (
          <div className="ferro-print-empty" data-testid="run-cards-empty">
            No boarding bookings for this date. Switch the date filter on the
            Bookings page or change the service filter.
          </div>
        ) : (
          <div className="ferro-print-stack">
            {cards.map((card) =>
              canEdit ? (
                // Sawyer 06 beds-lockers-runcard (B4) — a screen-only bar per card.
                <div key={card.cardId}>
                  <div className="ferro-print-card-bar">
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost"
                      onClick={() => setEditingId(card.cardId)}
                      aria-label={`Edit ${card.dogName}'s card`}
                      data-testid={`run-cards-edit-${card.cardId}`}
                    >
                      Edit card
                    </button>
                  </div>
                  <RunCard data={card} />
                </div>
              ) : (
                <RunCard key={card.cardId} data={card} />
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(layout, document.body);
}

export type { RunCardData };
